import React, { useState, useEffect } from 'react';
import {
  XMarkIcon,
  CheckIcon,
  MapPinIcon,
  TrashIcon,
  ArrowTurnDownLeftIcon,
  MagnifyingGlassIcon,
  ChevronDownIcon,
  ChevronUpIcon
} from '@heroicons/react/24/outline';
import { MapIcon } from '@heroicons/react/24/solid';
import MapPicker from './MapPicker';
import { exploreAPI } from '../services/api';

const MapModal = ({ 
  isOpen, 
  onClose, 
  value, 
  onChange,
  title = "Set Location",
  description = "Choose a location or create a trail for your project"
}) => {
  const [localValue, setLocalValue] = useState(value);
  const [mode, setMode] = useState('single');
  const [showSearch, setShowSearch] = useState(false);
  const [searchAddress, setSearchAddress] = useState('');
  const [searchCoordinates, setSearchCoordinates] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [searchResult, setSearchResult] = useState(null);

  // Initialize mode based on value
  useEffect(() => {
    if (value) {
      if (Array.isArray(value)) {
        setMode('trail');
      } else {
        setMode('single');
      }
    }
    setLocalValue(value);
  }, [value, isOpen]);

  const handleSave = () => {
    onChange?.(localValue);
    onClose();
  };

  const handleCancel = () => {
    setLocalValue(value); // Reset to original value
    onClose();
  };

  const handleModeChange = (newMode) => {
    setMode(newMode);
    setLocalValue(null); // Clear value when switching modes
  };

  const handleMapChange = (newValue) => {
    setLocalValue(newValue);
  };

  // Helper functions for trail mode actions
  const handleRemoveLastWaypoint = () => {
    if (Array.isArray(localValue) && localValue.length > 0) {
      const newValue = localValue.slice(0, -1);
      setLocalValue(newValue.length > 0 ? newValue : null);
    }
  };

  const handleClear = () => {
    setLocalValue(null);
  };

  // Search functionality
  const handleSearch = async () => {
    if (!searchAddress.trim() && !searchCoordinates.trim()) {
      setSearchError('Please enter an address or coordinates');
      return;
    }

    setIsSearching(true);
    setSearchError('');

    try {
      let query, searchType;
      
      if (searchAddress.trim()) {
        query = searchAddress.trim();
        searchType = 'address';
      } else {
        query = searchCoordinates.trim();
        searchType = 'coordinates';
      }

      const response = await exploreAPI.searchLocation(query, searchType);
      const data = response.data;
      
      if (data.success && data.location) {
        const { lat, lng } = data.location;
        
        // Set the search result for map centering
        setSearchResult({ lat, lng });
        
        // Set the location based on current mode
        if (mode === 'single') {
          setLocalValue({ lat, lng });
        } else {
          // For trail mode, add as a new waypoint
          const newWaypoint = { lat, lng };
          const currentTrail = Array.isArray(localValue) ? localValue : [];
          setLocalValue([...currentTrail, newWaypoint]);
        }
        
        // Close search panel
        setShowSearch(false);
        setSearchAddress('');
        setSearchCoordinates('');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.message || 'Search failed';
      setSearchError(errorMessage);
    } finally {
      setIsSearching(false);
    }
  };

  const handleCloseSearch = () => {
    setShowSearch(false);
    setSearchAddress('');
    setSearchCoordinates('');
    setSearchError('');
    setSearchResult(null);
  };


  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[999999]">
      <div className="bg-white dark:bg-gray-900 w-full h-full shadow-2xl flex flex-col overflow-hidden border border-gray-200/20 dark:border-gray-700/20" style={{ height: '100dvh', maxHeight: '100dvh', paddingBottom: 'env(safe-area-inset-bottom)' }}>
        
        {/* Header - Minimalist Design */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-gray-100 dark:border-gray-800 flex-shrink-0 bg-white dark:bg-gray-900">
          <div className="flex items-center gap-4">
            <h1 className="text-lg font-semibold text-gray-900 dark:text-white tracking-tight">
              {title}
            </h1>
            
            {/* Status Indicator */}
            <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 dark:bg-gray-800 rounded-full">
              {localValue ? (
                <>
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                    {Array.isArray(localValue) 
                      ? `${localValue.length} waypoint${localValue.length !== 1 ? 's' : ''}`
                      : 'Location set'
                    }
                  </span>
                </>
              ) : (
                <>
                  <div className="w-2 h-2 bg-gray-300 dark:bg-gray-600 rounded-full"></div>
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-500">
                    No location
                  </span>
                </>
              )}
            </div>
          </div>
          
          <button
            onClick={handleCancel}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-colors group"
          >
            <XMarkIcon className="w-5 h-5 text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300" />
          </button>
        </div>

        {/* Toolbar - Unified Control Panel */}
        <div className="px-4 sm:px-6 py-3 bg-gray-50/50 dark:bg-gray-800/50 border-b border-gray-100 dark:border-gray-800 flex-shrink-0">
          <div className="flex items-center justify-between gap-2">
            {/* Primary Actions */}
            <div className="flex items-center gap-2 min-w-0 flex-1">
              {/* Mode Toggle - Segmented Control */}
              <div className="flex bg-gray-100 dark:bg-gray-700 rounded-xl p-1">
                <button
                  type="button"
                  onClick={() => handleModeChange('single')}
                  className={`flex items-center gap-2 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 min-h-[44px] ${
                    mode === 'single'
                      ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  <MapPinIcon className="w-4 h-4" />
                  Single
                </button>
                <button
                  type="button"
                  onClick={() => handleModeChange('trail')}
                  className={`flex items-center gap-2 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 min-h-[44px] ${
                    mode === 'trail'
                      ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  <MapIcon className="w-4 h-4" />
                  Trail
                </button>
              </div>

              {/* Search Toggle */}
              <button
                type="button"
                onClick={() => setShowSearch(!showSearch)}
                className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-4 rounded-xl text-xs sm:text-sm font-medium transition-all duration-200 ${
                  showSearch
                    ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                <MagnifyingGlassIcon className="w-4 sm:h-4" />
                <span className="hidden sm:inline">Search</span>
                {showSearch ? (
                  <ChevronUpIcon className="w-3 h-3" />
                ) : (
                  <ChevronDownIcon className="w-3 h-3" />
                )}
              </button>
            </div>

            {/* Secondary Actions - Context Sensitive */}
            <div className="flex items-center gap-1">
              {mode === 'trail' && Array.isArray(localValue) && localValue.length > 0 && (
                <>
                  <button
                    type="button"
                    onClick={handleRemoveLastWaypoint}
                    className="flex items-center justify-center w-8 h-8 text-orange-600 dark:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/20 rounded-lg text-sm font-medium transition-colors"
                    title="Remove last waypoint"
                  >
                    <ArrowTurnDownLeftIcon className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={handleClear}
                    className="flex items-center justify-center w-8 h-8 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg text-sm font-medium transition-colors"
                    title="Clear all waypoints"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Search Panel - Progressive Disclosure */}
        {showSearch && (
          <div className="border-b border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 px-4 sm:px-6 py-4">
            <div className="max-w-2xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Address Search */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Address Search
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={searchAddress}
                      onChange={(e) => setSearchAddress(e.target.value)}
                      placeholder="Paris, France"
                      className="w-full pl-10 pr-4 py-3 border border-gray-200 dark:border-gray-700 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white transition-all"
                      onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    />
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <MapPinIcon className="w-4 h-4 text-gray-400" />
                    </div>
                  </div>
                </div>

                {/* Coordinate Search */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Coordinate Search
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={searchCoordinates}
                      onChange={(e) => setSearchCoordinates(e.target.value)}
                      placeholder="48.8566, 2.3522"
                      className="w-full pl-10 pr-4 py-3 border border-gray-200 dark:border-gray-700 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white transition-all"
                      onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    />
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <div className="w-4 h-4 border border-gray-400 rounded-sm"></div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Error Message */}
              {searchError && (
                <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                    <span className="text-sm text-red-700 dark:text-red-300 font-medium">
                      {searchError}
                    </span>
                  </div>
                </div>
              )}

              {/* Search Actions */}
              <div className="mt-4 flex items-center justify-between">
                {/* <div className="text-xs text-gray-500 dark:text-gray-400">
                  {mode === 'single' ? 'Will set location' : 'Will add waypoint to trail'}
                </div> */}
                <div></div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleCloseSearch}
                    className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 text-sm font-medium transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSearch}
                    disabled={isSearching || (!searchAddress.trim() && !searchCoordinates.trim())}
                    className="px-6 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2 shadow-sm"
                  >
                    {isSearching ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Searching...
                      </>
                    ) : (
                      <>
                        <MagnifyingGlassIcon className="w-4 h-4" />
                        Search
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Map Content - Full Focus */}
        <div className="flex-1 overflow-hidden relative">
          <MapPicker
            value={localValue}
            onChange={handleMapChange}
            mode={mode}
            onModeChange={handleModeChange}
            height="100%"
            className="h-full"
            hideControls={true}
            searchResult={searchResult}
          />
          
          {/* Contextual Instructions Overlay */}
          {/* <div className="absolute top-4 left-4 right-4 pointer-events-none z-[999999]">
            <div className="bg-white/95 dark:bg-gray-900/95 rounded-xl p-3 shadow-lg border border-gray-200/20 dark:border-gray-700/20">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {mode === 'single' 
                    ? 'Click anywhere on the map to set location'
                    : 'Click multiple points to create your trail route'
                  }
                </span>
              </div>
            </div>
          </div> */}
        </div>

        {/* Footer - Minimalist Actions */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-t border-gray-100 dark:border-gray-800 flex-shrink-0 bg-white dark:bg-gray-900">
          <div className="hidden sm:block">
            <div className="text-sm text-gray-500 dark:text-gray-400">
              {localValue ? (
                Array.isArray(localValue) ? (
                  <span>Trail with {localValue.length} waypoint{localValue.length !== 1 ? 's' : ''}</span>
                ) : (
                  <span>Location: {localValue.lat.toFixed(4)}, {localValue.lng.toFixed(4)}</span>
                )
              ) : (
                <span>No location selected</span>
              )}
            </div>
          </div>
          
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <button
              type="button"
              onClick={handleCancel}
              className="flex-1 sm:flex-none px-4 sm:px-6 py-3 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 font-medium transition-colors text-sm sm:text-base"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={!localValue}
              className="flex-1 sm:flex-none px-4 sm:px-8 py-3 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white rounded-xl font-medium transition-all flex items-center justify-center gap-2 shadow-sm disabled:bg-gray-300 disabled:cursor-not-allowed disabled:shadow-none text-sm sm:text-base"
            >
              <CheckIcon className="w-4 h-4" />
              <span className="truncate">Save Location</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MapModal;
